<template lang="pug">
#bluesearch.search
  TransitionNavigate
    template(v-if="loaded")
      slot(
        name="default",
        :loaded="loaded",
        :reloading="reloading",
        :executeSearch="executeSearch",
        :results="results",
        :extend="extend",
        :extended="extended")
        .search__layout(:data-hide-rail="config.hideRail")
          slot(name="header")
            header.search__header(v-if="config.showQuery")
              SearchQuery
          slot(name="rail" v-if="!config.hideRail")
            aside.search__rail(v-if="config.showFilters")
              SearchFilters
          slot(name="main")
            section.search__main
              slot(name="tags")
                SearchTags
              slot(name="results")
                SearchResults
          slot(name="footer")
            footer.search__footer
              SearchPagination

    template(v-else-if="errored")
      slot(name="error", :errored="errored", :errorType="errorType", :errorData="errorData")
        p There was an error

    template(v-else)
      slot(name="loader", :loaded="loaded")
        SearchLoader
</template>


<script>
import TransitionNavigate from "./transitions/Navigate.vue";
import SearchLoader from "./components/loader/Loader.vue";
export default {
  name: "BlueSearch",
  components: { TransitionNavigate, SearchLoader },
  data() {
    return {
      preSearch: null
    }
  },
  computed: {
    reloading() {
      return this.$store.getters.reloading;
    },
    loaded() {
      return this.$store.getters.loaded && !this.$store.getters.errored;
    },
    errored() {
      return this.$store.getters.errored;
    },
    errorType() {
      return this.$store.getters.errorType;
    },
    errorData() {
      return this.$store.getters.errorData;
    },
    results() {
      return this.$store.getters.results;
    },
    extended() {
      return this.$store.getters.extended;
    },
  },
  methods: {
    executeSearch(callback) {
      return this.$store.dispatch("executeSearch", callback);
    },
    extend(callback) {
      this.$store.commit("extend", callback);
    },
  },
  created() {
    this.$store.commit("applyOptions", this.config);
    this.$store.commit("parseInitialQueryParams");

    if (!this.config.allowPreSearch) return;

    this.preSearch = new Promise(resolve => {
      let isResolved = false;
      const done = () => {
        isResolved = true;
        resolve();
      };

      this.$emit('presearch', {
        done,
        extend: this.extend,
        extended: this.extended
      });

      setTimeout(() => {
        if (!isResolved) {
          done();
        };
      }, 1500);
    });
  },
  async mounted() {
    if (this.config.allowPreSearch) {
      await this.preSearch;
    }
    this.$store.dispatch("executeSearch");
  },
};
</script>



<style lang="scss">
@import 'scss/main.scss';

#bluesearch {
  // --focus-color: #{$blue};

  --font-size: #{$fontSizeBase};
  --font-weight: #{$fontWeightBase};
  // --font-family: #{$fontFamilyBase};
  --line-height: #{$lineHeightBase};
  --color: #{$black};

  // --reset-color: #{$blue};
  --reset-hover-color: #{darken($blue, 20%)};
  --reset-transition: color 0.2s ease;

  // --link-color: #{$blue};
  // --link-hover-color: #{darken($blue, 20%)};
  --link-transition: color 0.2s ease;

  // --loader-color: #{$blue};

  --accordion-title-color: #{$black};
  --accordion-title-active-color: #{$blue};
  --accordion-title-transition: color 0.2s ease;

  --border-color: #{$gray};

  --label-color: #{$black};
  --label-background: #{$gray};

  --button-color: #{$black};
  --button-background: #{$gray};
  --button-hover-color: #{$white};
  // --button-hover-background: #{$blue};
  --button-transition: color 0.2s ease, background 0.2s ease;

  --check-background: #{transparent};
  --check-border: #{$gray};
  --check-active-color: #{$white};
  --check-active-background: #{$blue};
  --check-active-border: #{$blue};
  --check-transition: color 0.2s ease, background 0.2s ease, border 0.2s ease;

  --tag-color: #{$black};
  --tag-background: #{$gray};
  --tag-hover-color: #{$white};
  --tag-hover-background: #{$red};
  --tag-transition: color 0.2s ease, background 0.2s ease;

  --pagination-nav-color: #{$white};
  --pagination-nav-background: #{$darkGray};
  --pagination-nav-hover-color: #{$white};
  --pagination-nav-hover-background: #{$blue};
  --pagination-nav-disabled-opacity: 0.2;
  --pagination-nav-transition: color 0.2s ease, background 0.2s ease;

  --pagination-number-color: #{$darkGray};
  --pagination-number-background: transparent;
  --pagination-number-hover-color: #{$darkGray};
  --pagination-number-hover-background: #{$gray};
  --pagination-number-active-color: #{$white};
  --pagination-number-active-background: #{$blue};
  --pagination-number-transition: color 0.2s ease, background 0.2s ease;
}

#bluesearch {
  *:focus {
    outline: none;
    box-shadow: inset 0 0 0 0.125rem var(--focus-color);
  }
}
</style>

<style lang="scss" scoped>
#bluesearch {
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  font-family: var(--font-family);
  line-height: var(--line-height);
  color: var(--color);
  box-sizing: border-box;

  &::v-deep *,
  &::v-deep *::before,
  &::v-deep *::after {
    box-sizing: inherit;
  }

  &::v-deep a {
    transition: var(--link-transition);
    color: var(--link-color);

    &:hover {
      color: var(--link-hover-color);
    }
  }
}

.search {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;

  &__layout {
    display: flex;
    gap: 2rem;
    flex-direction: column;
    flex: 1 1 100%;
    max-width: 100%;

    @include mq(lg) {
      display: grid;
      gap: 2rem 2rem;
      grid-template-columns: minmax(15rem, 1fr) 3fr;
      grid-template-rows: auto auto 1fr auto;
      grid-template-areas:
        "head head"
        "rail main"
        "rail foot";
    }

    &[data-hide-rail] {
      @include mq(lg) {
        grid-template-columns: 1fr;
        grid-template-areas:
          "head"
          "main"
          "foot";
      }
    }
  }

  &__header {
    grid-area: head;
    display: flex;
    max-width: 100%;
    overflow: hidden;
    padding: 3px;
    margin: -3px;
  }

  &__rail {
    grid-area: rail;
    display: flex;
    align-items: flex-start;
    max-width: 100%;
    overflow: hidden;
    padding: 3px;
    margin: -3px;
  }

  &__main {
    grid-area: main;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 100%;
    overflow: hidden;
    padding: 3px;
    margin: -3px;
  }

  &__footer {
    grid-area: foot;
    display: flex;
    flex-direction: column;
    max-width: 100%;
    overflow: hidden;
    padding: 3px;
    margin: -3px;
  }
}
</style>